import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    startSession,
    getSession,
    submitAnswer,
    getActiveQuestionnaires,
    linkInquiry,
    goBack, // Imported
    sendEstimateEmail
} from '../services/estimateApi';
import { createInquiry } from '../../api/inquiries';

const SESSION_KEY_STORAGE = 'auto_estimate_session_key';

export const useEstimateSession = () => {
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(true);
    const [error, setError] = useState(null);
    const [sessionKey, setSessionKey] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentAnswer, setCurrentAnswer] = useState(null); // New state
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false); // Track if email was already sent
    const [result, setResult] = useState(null);
    const [availableVersions, setAvailableVersions] = useState([]); // New
    const [activeVersionId, setActiveVersionId] = useState(null); // Track active version
    // Cache of every currently-active questionnaire so we can resolve the
    // user-facing label for the in-progress session even when we resumed it
    // from localStorage and never went through the selection screen.
    const [allVersions, setAllVersions] = useState([]);

    // Get versionId from navigation state (passed from Use Cases page)
    const location = useLocation();
    const stateVersionId = location.state?.versionId;
    const stateConsumedRef = useRef(false); // Track if state was already used

    // Initialize: Check localStorage or fetch versions
    useEffect(() => {
        const init = async () => {
            try {
                // Cache all active versions up front. The QuestionnaireFlow
                // header pill needs the label even on the resume path that
                // never visits the selection screen.
                try {
                    const v = await getActiveQuestionnaires();
                    setAllVersions(v || []);
                } catch (_) { /* non-fatal — pill will simply be hidden */ }

                const storedKey = localStorage.getItem(SESSION_KEY_STORAGE);
                const stateVer = stateVersionId ? parseInt(stateVersionId, 10) : null;

                if (storedKey) {
                    try {
                        const data = await getSession(storedKey);
                        // Resolve restored session's versionId from the most reliable
                        // source available (session row, then current question relation).
                        const restoredVersionId =
                            data.session?.versionId ?? data.currentQuestion?.versionId ?? null;

                        // The user just clicked a version-linked CTA (case shortcut /
                        // solution modal floater) that targets a different version than
                        // the in-progress session. Without this branch the stored
                        // session would silently win and the explicit click would feel
                        // broken. Confirm so a deep in-progress session isn't lost
                        // without consent, then drop the navigation state so a refresh
                        // doesn't reopen the dialog on every reload.
                        if (
                            stateVer &&
                            restoredVersionId &&
                            stateVer !== restoredVersionId &&
                            !stateConsumedRef.current
                        ) {
                            stateConsumedRef.current = true;
                            const proceed = window.confirm(
                                '이미 진행 중인 다른 견적이 있습니다.\n선택하신 견적으로 새로 시작하시겠습니까?\n(현재 진행 중인 답변은 사라집니다.)'
                            );
                            try {
                                window.history.replaceState({}, document.title);
                            } catch (_) { /* harmless */ }

                            if (proceed) {
                                localStorage.removeItem(SESSION_KEY_STORAGE);
                                await startNewSession(stateVer);
                                return;
                            }
                            // User declined — fall through and resume the stored session.
                        }

                        setSessionKey(storedKey);
                        setCurrentQuestion(data.currentQuestion);
                        setCurrentAnswer(data.currentAnswer);
                        setResult(data.resultJson);
                        setTotalQuestions(data.totalQuestions || 20);

                        if (restoredVersionId) {
                            setActiveVersionId(restoredVersionId);
                        }

                        if (data.status === 'COMPLETED' || data.status === 'SUBMITTED') {
                            setIsCompleted(true);
                        }
                        if (data.status === 'SUBMITTED') {
                            setIsSubmitted(true);
                        }
                    } catch (e) {
                        console.warn('Failed to restore session, starting new one', e);
                        localStorage.removeItem(SESSION_KEY_STORAGE);
                        await initSessionStart();
                    }
                } else {
                    await initSessionStart();
                }
            } catch (err) {
                setError(err);
            } finally {
                setInitializing(false);
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initSessionStart = async () => {
        setLoading(true);
        try {
            // 1. Get Active Questionnaires
            const versions = await getActiveQuestionnaires();
            setAllVersions(versions || []); // keep cache in sync if init ran via this path
            if (!versions || versions.length === 0) {
                setAvailableVersions([]);
                return;
            }

            if (versions.length === 1) {
                // Auto-start if only 1
                await startNewSession(versions[0].id);
            } else if (stateVersionId && !stateConsumedRef.current) {
                // Auto-start if versionId passed via navigation state (only use once)
                stateConsumedRef.current = true; // Mark as consumed
                const targetId = parseInt(stateVersionId, 10);
                const target = versions.find(v => v.id === targetId);
                if (target) {
                    await startNewSession(target.id);
                } else {
                    console.warn(`Version ID ${targetId} not found in active questionnaires.`);
                    setAvailableVersions(versions);
                }
            } else {
                // Multiple versions found, user must select
                setAvailableVersions(versions);
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const startNewSession = async (versionId) => {
        setLoading(true);
        try {
            const data = await startSession(versionId);
            localStorage.setItem(SESSION_KEY_STORAGE, data.session.sessionKey);
            setSessionKey(data.session.sessionKey);
            setActiveVersionId(versionId); // Store active version
            setCurrentQuestion(data.firstQuestion);
            setCurrentAnswer(data.currentAnswer || null); // Reset
            setTotalQuestions(data.totalQuestions || 0);
            setIsCompleted(false);
            setResult(null);
            setAvailableVersions([]); // clear selection state
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const submit = async (answerData) => {
        if (!sessionKey) return;
        // removed setLoading(true) for smooth transition
        try {
            const res = await submitAnswer(sessionKey, answerData);

            // Check if we have a new question
            if (res.nextQuestion) {
                setCurrentQuestion(res.nextQuestion);
            } else {
                setIsCompleted(true);
                setCurrentQuestion(null);
            }

            // Update result if provided
            if (res.resultJson) {
                setResult(res.resultJson);
            }
        } catch (err) {
            setError(err);
            alert('답변 전송에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const linkContactInquiry = async (inquiryId) => {
        if (!sessionKey) return;
        try {
            await linkInquiry(sessionKey, inquiryId);
        } catch (e) {
            console.error('Failed to link inquiry', e);
        }
    };

    const resetSession = async () => {
        localStorage.removeItem(SESSION_KEY_STORAGE);
        setSessionKey(null);
        setActiveVersionId(null);
        setCurrentQuestion(null);
        setIsCompleted(false);
        setIsSubmitted(false);
        setResult(null);
        // Re-initialize to fetch versions and allow selection again
        setInitializing(true);
        await initSessionStart();
        setInitializing(false);
    };

    const handleGoBack = async () => {
        if (!sessionKey) return;
        // removed setLoading(true)
        try {
            const res = await goBack(sessionKey);
            // Update current question
            // The API returns { status: 'success', currentQuestion: ... } or { status: 'check', ... }
            if (res.status === 'success' && res.currentQuestion) {
                setCurrentQuestion(res.currentQuestion);
                setIsCompleted(false); // In case we went back from completion screen (if applicable)
                setResult(null);
            } else {
                console.warn("Cannot go back further", res);
            }
        } catch (e) {
            setError(e);
            alert('이전 단계로 이동할 수 없습니다.');
        }
    };

    const restartCurrentSession = async () => {
        // Use activeVersionId if available, otherwise try currentQuestion or fallback to reset
        const vId = activeVersionId || currentQuestion?.versionId;
        if (vId) {
            await startNewSession(vId);
        } else {
            // Fallback if no version info found
            await resetSession();
        }
    };

    const submitInquiry = async (formData) => {
        if (!sessionKey) return false;
        try {
            const { email, phone, companyName, contactPerson, message, wantConsulting } = formData;
            let linkedInquiryId = null;

            // 1. Create Inquiry FIRST if requested (to get ID for the email link)
            if (wantConsulting) {
                try {
                    const newInquiry = await createInquiry({
                        email,
                        contactInfo: phone || '',
                        customerName: contactPerson || '',
                        companyName: companyName || '',
                        message: message || '견적 발송 후 자동 생성된 상담 요청입니다.',
                        type: 'SELFQUOTE',
                        status: 'UNREAD'
                    });

                    if (newInquiry && newInquiry.id) {
                        linkedInquiryId = newInquiry.id;
                        await linkInquiry(sessionKey, linkedInquiryId);
                        console.log('[useEstimateSession] Inquiry linked:', linkedInquiryId);
                    }
                } catch (e) {
                    console.error("Inquiry creation/link failed (non-blocking)", e);
                }
            }

            // 2. Send Estimate Email (Primary Goal) with full customer info AND inquiryId
            const emailRes = await sendEstimateEmail(sessionKey, {
                email,
                companyName,
                contactPerson,
                phone,
                inquiry: message,
                wantConsulting,
                inquiryId: linkedInquiryId // Pass the ID for admin link generation
            });

            // Check success logic from emailRes?
            if (!emailRes || !emailRes.success) {
                throw new Error('Email send failed');
            }

            setIsSubmitted(true); // Mark as submitted after successful email
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    // Resolve the active version's full record (with label/name/description)
    // from the cached list. This is what the QuestionnaireFlow header pill
    // renders so the user can see which questionnaire they're filling out.
    const activeVersion = activeVersionId
        ? (allVersions.find(v => v.id === activeVersionId) || null)
        : null;

    return {
        loading: loading || initializing,
        error,
        currentQuestion,
        currentAnswer, // Exposed
        totalQuestions,
        progress: currentQuestion ? Math.min(100, Math.round(((currentQuestion.sortOrder - 1) / (totalQuestions || 1)) * 100)) : 100,
        isCompleted,
        isSubmitted,
        result,
        availableVersions,
        activeVersion,
        selectVersion: startNewSession,
        submitAnswer: submit,
        resetSession, // Original reset (Reselect)
        restartCurrentSession, // New (Restart)
        linkContactInquiry,
        goBack: handleGoBack, // Exposed
        submitInquiry // New Handler
    };
};
